import { AddRounded, ContentCopy, Delete, Edit, ExpandMore, InfoOutline, PersonAdd, Update, Upload } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Popover,
    Radio,
    Select,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import renderAvatar from "../../components/render-avatar";
import { Classroom } from "../../models/classroom";
import { useStudentActions } from "../../hooks/useStudentActions";
import BackdropLoader from "../../components/backdrop-loader";
import { newStudent } from "../../models/student";
import { RenderRichText } from "../../components/render-rich-text";
import { useSnackbar } from "../../components/snackbar";



function Overview() {
  const { classroom_id } = useParams<{ classroom_id: string }>();

  const {
    student,
    getStudent,
    classrooms,
    getClassrooms,
    updateRegNumber,
  } = useStudentActions();

  const [locStudent, setLocStudent] = useState(newStudent());
  const [updating, setUpdating] = useState(false);
  const [regNumber, setRegNumber] = useState("");

  const { showMessage } = useSnackbar();

  const classroom = classrooms?.[Number(classroom_id)];

  useEffect(() => {
    if (!classroom) {
      const loadClassrooms = async () => {
        await getClassrooms();
      };
      loadClassrooms();
    }
  }, [classroom, classroom_id]);

  useEffect(() => {
    if (classroom?.reg_number !== undefined) {
      setRegNumber(classroom.reg_number);
    }
  }, [classroom]);

  useEffect(() => {
    const stored = localStorage.getItem("student");

    if (stored) {
      const parsed = JSON.parse(stored);
      setLocStudent(parsed);
    } else {
      const loadStudent = async () => {
        await getStudent();
      };
      loadStudent();
    }
  }, [student]);

  const updateRegNumberLoc = async () => {
    if (!regNumber?.trim()) {
      showMessage("Enter Reg Number", 1);
      return;
    }

    setUpdating(true);
    await updateRegNumber(Number(classroom_id), regNumber);
    setUpdating(false);
  };

  if (!classroom) {
    return (<BackdropLoader/>);
  }
  
  const accessExpired = !!classroom.expiry_date && new Date() >= new Date(classroom.expiry_date);

  return (
    <Box>
  
      <Stack spacing={3}>
        {/* Header Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          {renderAvatar(classroom.id, classroom.name, classroom.image, 80, 80)}
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {classroom.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {classroom.hub_name}
            </Typography>
          </Box>
        </Box>

        {/* Enrollment Expiry Info */}
        {classroom.expiry_date && (
          <Box
            sx={{
              bgcolor: "grey.50",
              borderRadius: 1,
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant="body2" fontWeight={500}>
              Enrollment Expires On
            </Typography>
            <Chip
              label={new Date(classroom.expiry_date).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric"
              })}
              color="warning"
              size="small"
              variant="outlined"
            />
          </Box>
        )}

        {/* Notice Board */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Notice Board
          </Typography>
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }} />
          <RenderRichText jsonContent={classroom.description} />
        </Box>
      </Stack>
 

      {/* Student Info */}
      <Box mt={4}>

        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            You
          </Typography>
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 2 }} />
        </Box>

        <Stack
          spacing={2}
          alignItems="flex-start"
        >
          <TextField
            value={locStudent.name || "Your Name"}
            disabled
            fullWidth
            InputProps={{ sx: { bgcolor: "grey.50" } }}
          />

          <TextField
            label="Registration Number"
            value={regNumber || ""}
            onChange={(e) => setRegNumber(e.target.value)}
            fullWidth
            InputProps={{ sx: { bgcolor: "grey.50" } }}
          />


          <Button
            onClick={updateRegNumberLoc}
            variant="contained"
            disabled={updating || accessExpired}
            fullWidth
            size="large"
            sx={{
              mt: 3,
              textTransform: "none",
              fontSize: "1.1rem",
              fontWeight: 600,
              py: 1.5,
              borderRadius: 2,
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8, #6b46c1)"
              }
            }}
          >
            {updating ? <CircularProgress size={16} color="inherit" /> : "Update Reg. Number"}
          </Button>
        </Stack>

        </Box>
      </Box>
  );
}

export default memo(Overview);